import React, { useState } from 'react'

import { getErrorMessage, getPersonLabel } from 'utils'

import type { Owner } from 'store/owners/types'

import { useSnackbar } from 'contexts'
import { useDeleteOwner, useArchiveOwner } from 'hooks/api'

import { useTheme, Stack, Typography, Avatar } from '@mui/material'

import { Modal } from '@repo/ui'
import { Button } from '@repo/ui'
import { Alert } from '@repo/ui'

interface DeleteOwnerModalProps {
  owner: Owner | null
  open: boolean
  onClose: () => void
  refetch: () => void
  onDeleted?: () => void
}

type ModalView = 'delete' | 'archive-pivot'

export const DeleteOwnerModal = (props: DeleteOwnerModalProps) => {
  const { owner, open, onClose, refetch, onDeleted } = props
  // VERA-304: name if there is one, else the email as the primary line.
  const ownerLabel = getPersonLabel(owner)
  const theme = useTheme()

  const { open: notification } = useSnackbar()
  const { deleteOwner, isLoading: isDeleting } = useDeleteOwner()
  const { archiveOwner, isLoading: isArchiving } = useArchiveOwner()

  const [error, setError] = useState<string | null>(null)
  const [view, setView] = useState<ModalView>('delete')

  const isLoading = isDeleting || isArchiving

  const handleDeleteOwner = async () => {
    if (!owner) return

    setError(null)

    try {
      const result = await deleteOwner(owner.id)

      if (result.success && result.data) {
        if (result.data.alreadyDeleted) {
          notification({
            type: 'info',
            title: 'Owner already deleted',
          })
        } else {
        }

        handleClose()
        onDeleted?.()
        refetch()
      } else {
        // Check for active-certificates conflict — pivot to archive offer
        const errCode = (result.error as any)?.response?.data?.code || (result.error as any)?.code
        if (errCode === 'OWNER_HAS_ACTIVE_CERTIFICATES') {
          setView('archive-pivot')
        } else {
          setError(getErrorMessage(result.error || 'Failed to delete owner'))
        }
      }
    } catch (err: any) {
      const errCode = err?.response?.data?.code || err?.code
      if (errCode === 'OWNER_HAS_ACTIVE_CERTIFICATES') {
        setView('archive-pivot')
      } else {
        setError(getErrorMessage(err))
      }
    }
  }

  const handleArchiveInstead = async () => {
    if (!owner) return
    setError(null)

    try {
      const result = await archiveOwner(owner.id)
      if (result.success) {
        notification({
          type: 'success',
          title: 'Owner archived',
        })
        handleClose()
        refetch()
      } else {
        setError(getErrorMessage(result.error || 'Failed to archive owner'))
      }
    } catch (err) {
      setError(getErrorMessage(err))
    }
  }

  const handleClose = () => {
    setError(null)
    setView('delete')
    onClose()
  }

  const ownerCard = owner && (
    <Stack
      direction="row"
      alignItems="center"
      spacing={2}
      sx={{
        p: 3,
        border: `1px solid ${theme.palette.grey[200]}`,
        borderRadius: 2,
        backgroundColor: theme.palette.common.white,
      }}
    >
      <Avatar
        src={owner.avatar || undefined}
        sx={{
          width: 40,
          height: 40,
          bgcolor: theme.palette.primary.main,
          fontSize: 14,
          fontWeight: 600,
        }}
      >
        {ownerLabel.primary?.charAt(0).toUpperCase() || '?'}
      </Avatar>
      <Stack spacing={0}>
        <Typography variant="body2" fontWeight="500" color="text.primary">
          {ownerLabel.primary || '—'}
        </Typography>
        {/* VERA-304: suppressed when the address IS the primary line above, so a
            nameless owner is not shown their own email twice. */}
        {ownerLabel.secondary && (
          <Typography variant="caption" color="text.secondary">
            {ownerLabel.secondary}
          </Typography>
        )}
      </Stack>
    </Stack>
  )

  if (view === 'archive-pivot') {
    return (
      <Modal
        title="Owner has active certificates"
        open={open}
        onClose={handleClose}
        actions={
          <>
            <Button
              disableElevation
              variant="secondary"
              onClick={handleClose}
              disabled={isLoading}
            >
              Cancel
            </Button>

            <Button
              disableElevation
              onClick={handleArchiveInstead}
              disabled={isLoading}
            >
              {isLoading ? 'Archiving...' : 'Archive instead'}
            </Button>
          </>
        }
      >
        <Stack spacing={4}>
          <Typography variant="body2" color="text.secondary">
            This owner has active certificates and cannot be deleted. You can archive them instead —
            they will be hidden from the default list and can be unarchived at any time.
          </Typography>

          {error && <Alert severity="error" description={error} />}

          {ownerCard}
        </Stack>
      </Modal>
    )
  }

  return (
    <Modal
      title="Delete owner"
      open={open}
      onClose={handleClose}
      actions={
        <>
          <Button
            disableElevation
            variant="secondary"
            onClick={handleClose}
            disabled={isLoading}
          >
            Cancel
          </Button>

          <Button
            disableElevation
            destructive
            onClick={handleDeleteOwner}
            disabled={isLoading}
          >
            Delete
          </Button>
        </>
      }
    >
      <Stack spacing={4}>
        <Typography variant="body2" color="text.secondary">
          Are you sure you want to delete the following owner?
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {ownerCard}
      </Stack>
    </Modal>
  )
}
